import { CacheTagGroup } from "../services/cache-types";

/**
 * Formats a serialized .NET TimeSpan ("[d.]hh:mm:ss[.fffffff]") as a short
 * human-readable duration, e.g. "850 ms", "12.3 s", "2m 5s".
 */
export const formatDuration = (value: string | null | undefined): string => {
  if (!value) return "-";

  const match = /^(?:(\d+)\.)?(\d+):(\d+):(\d+(?:\.\d+)?)$/.exec(value);
  if (!match) return value;

  const [, days, hours, minutes, seconds] = match;
  const totalSeconds =
    Number(days ?? 0) * 86400 +
    Number(hours) * 3600 +
    Number(minutes) * 60 +
    Number(seconds);

  if (totalSeconds < 1) return `${Math.round(totalSeconds * 1000)} ms`;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)} s`;

  const wholeMinutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.round(totalSeconds % 60);
  return `${wholeMinutes}m ${remainingSeconds}s`;
};

export const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return "unknown size";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

/**
 * Narrows tag groups to those whose tag matches the search term, or that
 * contain keys matching it (in which case only the matching keys are kept).
 */
export const filterTagGroups = (
  groups: CacheTagGroup[],
  searchTerm: string,
): CacheTagGroup[] => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return groups;

  return groups.reduce<CacheTagGroup[]>((acc, group) => {
    if (group.tag.toLowerCase().includes(term)) {
      acc.push(group);
      return acc;
    }

    const keys = group.keys.filter((key) => key.toLowerCase().includes(term));
    if (keys.length > 0) acc.push({ ...group, keys });
    return acc;
  }, []);
};
